import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import bill components (lazy-loaded for performance)
const Bills = React.lazy(() => import("./Bills"));
const NewBill = React.lazy(() => import("./NewBill"));
const BillDetails = React.lazy(() => import("./BillDetails"));
const BillPDFPreview = React.lazy(() => import("./BillPDFPreview"));
const PaymentReceivingDetails = React.lazy(() => import("./PaymentReceivingDetails"));
const EditPaymentDetails = React.lazy(() => import("./EditPaymentDetails"));
const CompanyDetails = React.lazy(() => import("./CompanyDetails"));

const billsModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "bills",
    name: "Simple Bills",
    description: "Create, send, and track invoices and bills",
    version: "1.2.0",
    enabled: true,
    category: "billing",
    order: 6,
    icon: "💳",
    color: "#E53E3E",

    // 🛣️ ROUTES
    routes: [
        {
            path: "/bills",
            component: Bills,
            permissions: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"]
        },
        {
            path: "/bills/new",
            component: NewBill,
            permissions: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"]
        },
        {
            path: "/bills/payment-details",
            component: PaymentReceivingDetails,
            permissions: ["BILLS_ADMIN", "ADMIN"]
        },
        {
            path: "/bills/payment-details/edit",
            component: EditPaymentDetails,
            permissions: ["BILLS_ADMIN", "ADMIN"]
        },
        {
            path: "/bills/company-details",
            component: CompanyDetails,
            permissions: ["BILLS_ADMIN", "ADMIN"]
        },
        {
            path: "/bill/:id",
            component: BillDetails,
            permissions: [] // Public route - bills can be viewed by anyone with the link (for client payment)
        },
        {
            path: "/bill/:id/preview",
            component: BillPDFPreview,
            permissions: [] // Public route - no authentication required
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Bills",
            path: "/bills",
            icon: "📄",
            permissions: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"]
        },
        {
            label: "New Bill",
            path: "/bills/new",
            icon: "➕",
            permissions: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"]
        },
        {
            label: "Payment Details",
            path: "/bills/payment-details",
            icon: "💳",
            permissions: ["BILLS_ADMIN", "ADMIN"]
        },
        {
            label: "Company Details",
            path: "/bills/company-details",
            icon: "🏢",
            permissions: ["BILLS_ADMIN", "ADMIN"]
        }
    ],

    // 🔐 PERMISSIONS
    permissions: {
        view: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"],
        create: ["BILLS_ADMIN", "BILLS_USER", "ADMIN"],
        edit: ["BILLS_ADMIN", "ADMIN"],
        delete: ["BILLS_ADMIN", "ADMIN"]
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Create Bill",
            path: "/bills/new",
            icon: "➕",
            description: "Create a new invoice or bill"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.2.0",
            releaseDate: "2025-11-22",
            title: "Production Release - Enhanced Authentication & Module History",
            features: [
                "✅ Comprehensive changelog history documentation",
                "✨ Enhanced authentication module integration",
                "🔨 Improved module configuration structure",
                "✨ Enhanced dark mode color handling across bill components"
            ],
            notes: "Production-ready release with complete feature history and authentication improvements."
        },
        {
            version: "1.1.0",
            releaseDate: "2025-09-15",
            title: "Tenant Model Migration & Company Details Refactor",
            features: [
                "🔨 Refactored payment receiving details to tenant model (815 lines)",
                "✅ Renamed BillingDetails to CompanyDetails component (363 lines)",
                "✨ Enhanced payment details editing functionality (182 lines)",
                "✨ Improved bill creation workflow (242 lines)",
                "🐛 Fixed bill visibility for non-authenticated users"
            ],
            notes: "Major architectural refactor moving to tenant-based payment model with enhanced company management."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-08-01",
            title: "Version 1.0 - Production Ready Release",
            features: [
                "✅ Bills module improvements and optimizations (318 lines)",
                "✨ Enhanced UI with better filtering and search",
                "🔨 Centralized markdown templates (295 lines)",
                "✨ Calendar integration with event types",
                "✨ Stripe bill payment component enhancements"
            ],
            notes: "Official 1.0 release with production-ready features and performance optimizations."
        },
        {
            version: "0.9.0",
            releaseDate: "2025-06-20",
            title: "Proposal Enhancements & Visual Effects",
            features: [
                "✨ Enhanced One Group proposal page with visual effects (184 lines)",
                "✅ Bill and project specifications improvements (282 lines)",
                "✨ Currency formatting in bill preview (28 lines)",
                "✅ Line item deletion and price formatting (103 lines)",
                "🔨 Enhanced bill details component for proposals"
            ],
            notes: "Major proposal functionality with enhanced visual presentation and formatting."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-04-10",
            title: "Public Bill Access & PDF Enhancements",
            features: [
                "✅ Public bill PDF preview with query support (138 lines)",
                "🔒 Modified permissions for public bill access",
                "✨ Enhanced video management with navigation improvements",
                "🔨 Theme migration and color mode enhancements (205 lines)",
                "✨ Module breadcrumb navigation across bill pages"
            ],
            notes: "Significant improvements to public bill sharing and PDF preview functionality."
        },
        {
            version: "0.7.0",
            releaseDate: "2025-02-15",
            title: "GraphQL Integration & Proposal Status",
            features: [
                "✅ GraphQL code generation and nullable field fixes",
                "✨ Enhanced bill management with proposal status (147 lines)",
                "✅ PDF export functionality for bills (119 lines)",
                "✨ Draft status toggle in bill details",
                "🔨 Refactored FloatingNavbar and Bills components (276 lines)"
            ],
            notes: "Major GraphQL integration with enhanced proposal and status management."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-12-01",
            title: "Payment & Billing Details System",
            features: [
                "✅ Billing details component (497 lines)",
                "✅ Payment details editing system (996 lines)",
                "✅ Payment receiving details (397 lines)",
                "🔨 Module configuration expansion (30 lines)",
                "✨ Payment method options in bill management (294 lines)"
            ],
            notes: "Complete payment and billing details management system with comprehensive editing capabilities."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-09-15",
            title: "Payment Integration & Multi-Currency Support",
            features: [
                "✅ Stripe bill payment component (390 lines)",
                "✅ Bank transfer payment option (356 lines)",
                "✅ Bitcoin/crypto payment integration (442 lines)",
                "✅ Payment method selector (201 lines)",
                "✅ Bill payment success page (342 lines)",
                "✨ Currency support across billing components (72 lines)"
            ],
            notes: "Major payment system integration with Stripe, bank transfers, and cryptocurrency support."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-06-20",
            title: "Brand Configuration & Contact Information",
            features: [
                "✅ Enhanced brandConfig integration (145 lines)",
                "✨ Improved contact information display in bills",
                "✨ Crypto discount feature for payment methods (123 lines)",
                "🔨 Enhanced subscription and billing UI",
                "✨ Responsive design improvements (38 lines)"
            ],
            notes: "Brand configuration and contact management with enhanced payment options."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-03-15",
            title: "PDF Generation & Preview System",
            features: [
                "✅ Bill PDF preview functionality (391 lines)",
                "✅ Bill PDF component with export (363 lines)",
                "✨ Enhanced BillDetails with PDF generation (133 lines)",
                "✨ Invoice terminology updates across components",
                "🔨 Authentication modal integration (25 lines)"
            ],
            notes: "Complete PDF generation and preview system with authentication handling."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-12-01",
            title: "Bill Chaser & Enhanced Features",
            features: [
                "✅ Bill Chaser functionality (741 lines)",
                "✨ Client information display on Bills page (47 lines)",
                "🔨 Enhanced bill details with improved navigation (502 lines)",
                "✨ Footer positioning adjustments",
                "✨ Website details editing integration"
            ],
            notes: "Bill chasing automation and enhanced client information management."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-09-01",
            title: "Initial Module Creation",
            features: [
                "✅ Bill details component (597 lines)",
                "✅ Bills list view (341 lines)",
                "✅ New bill creation form (499 lines)",
                "✅ Core module structure and routing",
                "✅ Basic CRUD operations for bills"
            ],
            notes: "Initial release with core bill management functionality including creation, viewing, and basic operations."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default billsModuleConfig;